import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaTwitter,
  FaLinkedinIn,
  FaFacebook,
  FaGithub,
  FaInstagram,
} from "react-icons/fa";

import {
  MdMail,
  MdPhone,
  MdLocationOn,
  MdAccessTime,
} from "react-icons/md";

import logo from "./logo.png";
import "./tailwindstyles.css";

const AboutUs = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section
      className={`relative w-full overflow-hidden bg-[#0b0f19] text-slate-300 
      ${isHome ? "py-32" : "pt-8 pb-16"}`}
    >
      {/* BACKGROUND GLOW WAVES */}
      <div className="absolute inset-0 opacity-40">
        <div
          className="absolute top-0 left-0 w-[120%] h-[120%]
          bg-gradient-to-r from-orange-600/40 via-yellow-400/20 to-sky-500/30
          blur-3xl animate-slowWave"
        />

        <div
          className="absolute bottom-0 right-0 w-[120%] h-[120%]
          bg-gradient-to-r from-sky-500/30 via-orange-300/10 to-yellow-400/30
          blur-3xl animate-fastWave"
        />
      </div>

      {/* ANIMATED LIGHT LINE */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-0 left-[-100%] w-[50%] h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-20deg] animate-lightMove" />
      </div>

      {/* GRID OVERLAY */}
      <div
        className="pointer-events-none absolute inset-0
        bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),
             linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)]
        bg-[size:60px_60px]"
      />

      {/* CONTENT */}
      <div className="relative z-10 container mx-auto px-custom max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* LEFT BLOCK */}
          <div className="md:col-span-4 space-y-10">
            {/* Logo + description */}
            <div className="transform transition-all duration-500 hover:scale-[1.02]">
              <div className="flex items-center space-x-4 mb-6 group">
                <div className="relative">
                  <div className="absolute inset-0 bg-orange-500/30 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition duration-500" />

                  <img
                    src={logo}
                    alt="Power Sense Logo"
                    className="relative w-12 h-12 rounded-xl bg-white p-1 shadow-[0_0_25px_rgba(255,140,0,0.25)]"
                  />
                </div>

                <div>
                  <h1 className="text-4xl font-extrabold bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-500 bg-clip-text text-transparent tracking-wide">
                    Power Sense
                  </h1>

                  <p className="text-[12px] uppercase tracking-[4px] text-slate-500 mt-1">
                    Smart Energy Platform
                  </p>
                </div>
              </div>

              <p className="text-[17px] leading-9 text-slate-300 max-w-lg">
                Empowering Smart Energy, IoT Automation, and Intelligent
                Monitoring for campuses, industries, and smart communities.
              </p>
            </div>

            {/* Address & Timing */}
            <div className="space-y-6">
              <div className="flex items-center group">
                <div className="mr-4">
                  <MdLocationOn className="text-orange-400 text-2xl group-hover:scale-110 transition duration-300" />
                </div>

                <p className="text-slate-300 group-hover:text-white transition">
                  IIT Hyderabad, India
                </p>
              </div>

              <div className="flex items-center group">
                <div className="mr-4">
                  <MdAccessTime className="text-sky-400 text-2xl group-hover:scale-110 transition duration-300" />
                </div>

                <p className="text-slate-300 group-hover:text-white transition">
                  Mon – Fri: 9:00 AM – 6:00 PM
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT BLOCK */}
          <div className="md:col-span-7 grid sm:grid-cols-2 md:grid-cols-3 gap-10">
            {/* COMPANY */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white relative inline-block">
                Company

                <span className="absolute left-0 -bottom-2 w-10 h-[2px] bg-orange-400 rounded-full animate-pulse" />
              </h3>

              <ul className="space-y-4">
                {[
                  { text: "About Us", path: "/Aboutus" },
                  { text: "Careers", path: "/careers" },
                  { text: "Contact", path: "/contact" },
                  { text: "Blog", path: "/features" },
                ].map((item) => (
                  <li key={item.text}>
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className="text-slate-300 hover:text-orange-400 transition-all duration-300 hover:translate-x-2"
                    >
                      {item.text}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* RESOURCES */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white relative inline-block">
                Resources

                <span className="absolute left-0 -bottom-2 w-10 h-[2px] bg-sky-400 rounded-full animate-pulse" />
              </h3>

              <ul className="space-y-4">
                {[
                  "Documentation",
                  "Case Studies",
                  "FAQs",
                  "Support",
                ].map((item) => (
                  <li
                    key={item}
                    className="text-slate-300 hover:text-sky-400 transition-all duration-300 hover:translate-x-2 cursor-pointer"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* CONNECT */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white relative inline-block">
                Connect

                <span className="absolute left-0 -bottom-2 w-10 h-[2px] bg-yellow-400 rounded-full animate-pulse" />
              </h3>

              <div className="flex items-center">
                <MdMail className="text-orange-400 text-xl mr-3" />

                <a
                  href="mailto:contact@powersense.in"
                  className="hover:text-orange-400 transition duration-300"
                >
                  contact@powersense.in
                </a>
              </div>

              <div className="flex items-center">
                <MdPhone className="text-sky-400 text-xl mr-3" />

                <a
                  href="tel:+919876543210"
                  className="hover:text-sky-400 transition duration-300"
                >
                  +91 8374993999
                </a>
              </div>

              {/* SOCIAL ICONS */}
              <div className="flex items-center space-x-4 text-xl">
                {[
                  FaLinkedinIn,
                  FaTwitter,
                  FaFacebook,
                  FaGithub,
                  FaInstagram,
                ].map((Icon, index) => (
                  <div
                    key={index}
                    className="w-11 h-11 rounded-full border border-white/10 bg-white/[0.03]
                    flex items-center justify-center cursor-pointer
                    hover:bg-orange-500 hover:border-orange-400 hover:text-white
                    hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(255,140,0,0.4)]
                    transition-all duration-300"
                  >
                    <Icon />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM */}
        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © 2024 Power Sense Pvt Ltd. All rights reserved.
          </p>

          <div className="flex items-center gap-6 text-sm text-slate-500">
            <span className="hover:text-orange-400 transition cursor-pointer">
              Privacy Policy
            </span>

            <span className="hover:text-orange-400 transition cursor-pointer">
              Terms & Conditions
            </span>

            <span className="hover:text-orange-400 transition cursor-pointer">
              Support
            </span>
          </div>
        </div>
      </div>

      {/* ANIMATIONS */}
      <style>{`
        @keyframes slowWave {
          0% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
          50% {
            transform: translate(10%, 10%) rotate(15deg);
          }
          100% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
        }

        @keyframes fastWave {
          0% {
            transform: translate(10%, 10%) rotate(0deg);
          }
          50% {
            transform: translate(-10%, -10%) rotate(-15deg);
          }
          100% {
            transform: translate(10%, 10%) rotate(0deg);
          }
        }

        @keyframes lightMove {
          0% {
            transform: translateX(-120%) skewX(-20deg);
          }
          100% {
            transform: translateX(250%) skewX(-20deg);
          }
        }

        .animate-slowWave {
          animation: slowWave 14s ease-in-out infinite;
        }

        .animate-fastWave {
          animation: fastWave 10s ease-in-out infinite;
        }

        .animate-lightMove {
          animation: lightMove 8s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default AboutUs;